import { Context } from '../context/Context';
import { Camera } from '../camera';
import { GBuffer } from './GBuffer';
import { Decal } from './Decal';
import { VertexBuffer } from '../mesh';
import { mat4 } from 'gl-matrix';
import { createCube } from '../geometry';

export class DecalRenderer {
  private vao!: WebGLVertexArrayObject;
  private buffer!: VertexBuffer;

  init(ctx: Context): void {
    const gl = ctx.gl;

    // Create unit cube geometry (position-only, no normals/UVs needed)
    const collection = createCube(1);
    const compiled = collection.compile(['position']);

    const glBuffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, glBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, compiled.data, gl.STATIC_DRAW);

    this.buffer = new VertexBuffer(
      glBuffer,
      gl.TRIANGLES,
      compiled.vertexStride,
      compiled.vertexCount,
    );

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, glBuffer);
    gl.enableVertexAttribArray(0); // aVertexPosition at location 0
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, compiled.vertexStride * 4, 0);
    gl.bindVertexArray(null);
  }

  render(ctx: Context, gBuffer: GBuffer, decals: Decal[], camera: Camera): void {
    const gl = ctx.gl;

    // Bind decal FBO: writes to G-buffer albedo, depth tests against G-buffer depth
    gBuffer.bindForDecals(gl);

    // GL state for decals:
    // - Depth test enabled (LEQUAL) so we only render where geometry exists
    // - No depth writing (don't modify the depth buffer)
    // - Front-face culling: render back faces so we get fragments even when camera is inside the volume
    // - Alpha blending to merge decal with existing albedo
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.depthMask(false);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.FRONT);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    const shader = ctx.engine.shaderManager.getDecalShader();
    ctx.setShader(shader);

    const viewProjection = mat4.mul(mat4.create(), camera.projectionMatrix, camera.modelViewMatrix);

    gl.bindVertexArray(this.vao);

    for (const decal of decals) {
      // Reset the sampler counter so texture uniforms reuse the same slots
      // rather than allocating new ones each iteration.
      shader.next();

      shader.uniformsContainer
        .setViewProjectionMatrix(viewProjection)
        .setDepthMap(gBuffer.depthTexture)
        .setInvViewProjection(camera.invViewProjection)
        .setViewportSize([ctx.viewPort.width, ctx.viewPort.height])
        .setModelMatrix(decal.modelMatrix)
        .setInverseModelMatrix(decal.inverseModelMatrix)
        .setDecalTexture(decal.texture)
        .setOpacity(decal.opacity);

      shader.validate();
      gl.drawArrays(gl.TRIANGLES, 0, this.buffer.itemCount);
    }

    // Restore state
    gl.cullFace(gl.BACK);
    gl.disable(gl.BLEND);
    gl.depthFunc(gl.LESS);
    gl.depthMask(true);

    gBuffer.unbind(gl);
  }

  destroy(gl: WebGL2RenderingContext): void {
    if (this.vao) gl.deleteVertexArray(this.vao);
    if (this.buffer?.glVertexBuffer) gl.deleteBuffer(this.buffer.glVertexBuffer);
  }
}
